import OpenAI from 'openai';
import { supabase } from './supabase';

async function getOpenAIKey(): Promise<string> {
  try {
    const { data, error } = await supabase
      .from('settings')
      .select('value')
      .eq('key', 'openai_api_key')
      .single();

    if (error) throw error;
    if (!data?.value) throw new Error('OpenAI API key not found in settings');

    return data.value;
  } catch (error) {
    console.error('Error fetching OpenAI API key:', error);
    throw error;
  }
}

let openaiInstance: OpenAI | null = null;

export async function getOpenAIInstance(): Promise<OpenAI> {
  if (!openaiInstance) {
    const apiKey = await getOpenAIKey();
    openaiInstance = new OpenAI({
      apiKey,
      dangerouslyAllowBrowser: true,
    });
  }
  return openaiInstance;
}

export async function generateFinancialInsights(data: any) {
  try {
    const openai = await getOpenAIInstance();
    const completion = await openai.chat.completions.create({
      messages: [
        {
          role: 'system',
          content:
            'You are a financial analyst AI assistant. Analyze the provided financial data and provide actionable insights.',
        },
        {
          role: 'user',
          content: `Please analyze this financial data and provide key insights: ${JSON.stringify(data)}`,
        },
      ],
      model: 'gpt-4-turbo-preview',
    });

    return completion.choices[0].message.content;
  } catch (error) {
    console.error('Error generating financial insights:', error);
    throw error;
  }
}

export async function suggestCostOptimizations(expenses: any) {
  try {
    const openai = await getOpenAIInstance();
    const completion = await openai.chat.completions.create({
      messages: [
        {
          role: 'system',
          content:
            'You are a cost optimization expert. Analyze the expense data and suggest ways to reduce costs.',
        },
        {
          role: 'user',
          content: `Please analyze these expenses and suggest optimizations: ${JSON.stringify(expenses)}`,
        },
      ],
      model: 'gpt-4-turbo-preview',
    });

    return completion.choices[0].message.content;
  } catch (error) {
    console.error('Error generating cost optimizations:', error);
    throw error;
  }
}

export async function generateInvoiceDescription(jobDetails: any) {
  try {
    const openai = await getOpenAIInstance();
    const completion = await openai.chat.completions.create({
      messages: [
        {
          role: 'system',
          content:
            'You are an expert at writing professional invoice descriptions. Create clear and detailed descriptions for invoices.',
        },
        {
          role: 'user',
          content: `Please generate a professional invoice description for this job: ${JSON.stringify(jobDetails)}`,
        },
      ],
      model: 'gpt-4-turbo-preview',
    });

    return completion.choices[0].message.content;
  } catch (error) {
    console.error('Error generating invoice description:', error);
    throw error;
  }
}
